import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetsFilters } from './PerMonthFilters';
import { AttendanceSheetsModals } from './PerMonthModals';
import { AttendanceSheetsTable } from './PerMonthTable';
import { MassNotificationModal } from '../../components/ModalNotificacaoMassa';
import { useAttendanceSheets } from '../perMonth/usePerMonth';
import { getAttendanceSheetIdFromJwt } from '@/modules/system/auth/application/auth.service';
import { getFolhaEstagiarioMesAtualPorToken } from '../../../attendance-sheet.service';
import { ehPerfilEstagiario, podeExibirNotificacoesFolhasEmMassa } from './perMonthFilters.utils';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { useContext } from 'react';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { AttendanceSheetsPerMonthPdf } from '../../components/AttendanceSheetsPerMonthPDF';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useNavigate } from 'react-router-dom';

export function AttendanceSheetsPerMonthPage() {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const navigate = useNavigate();
  const redirectEstagiarioRef = useRef(false);
  const { effectiveView, viewReady } = useNavigationView();
  const { sessionReady, authVisao } = context;

  const ehEstagiario = useMemo(
    () => ehPerfilEstagiario(authVisao, effectiveView),
    [authVisao, effectiveView],
  );

  const { setActions } = useBreadcrumbActions();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[] | null>(null);

  const tableState = useDataTableState('estagiario.nome');
  const {
    mesBusca,
    setMesBusca,
    statusBusca,
    setStatusBusca,
    filtroResponsavel,
    setFiltroResponsavel,
    filtroEstagiario,
    setFiltroEstagiario,
    filtrosStatus,
    registros,
    handleBuscar,
    comFolha,
    setComFolha,
    responsaveis,
    estagiarios,
    permissaoCriarFolhas,
    permissaoExcluirFolhas,
    isExecuting,
    handleCriarFolha,
    handleGenerateAttendanceSheetsForMonth,
    selectedItem,
    setSelectedItem,
    folhaSelecionada,
    setFolhaSelecionada,
    showModalCriarFolha,
    setShowModalCriarFolha,
    showModalCriarFolhas,
    setShowModalCriarFolhas,
    showModalNotificacaoIndividual,
    setShowModalNotificacaoIndividual,
    showModalNotificacoes,
    setShowModalNotificacoes,
    showModalResponsavel,
    setShowModalResponsavel,
    showModalAssinaturas,
    setShowModalAssinaturas,
    folhaParaAssinaturas,
    setFolhaParaAssinaturas,
    loaded,
    exibirFiltroResponsavel,
  } = useAttendanceSheets(tableState);

  useEffect(() => {
    if (redirectEstagiarioRef.current) return;
    if (!sessionReady) return;
    if (context.state?.username?.trim() && !viewReady) return;
    if (!ehEstagiario) return;

    redirectEstagiarioRef.current = true;

    const irParaFolha = (id: string) =>
      navigate(`${Links.Folha_Frequencia_Editar}/${id}`, { replace: true });

    if (!context.state?.username?.trim()) {
      void getFolhaEstagiarioMesAtualPorToken()
        .then(({ id }) => {
          if (id) irParaFolha(id);
          else navigate(Links.Estagiario_Minhas_Folhas, { replace: true });
        })
        .catch(() => {
          const idJwt = getAttendanceSheetIdFromJwt();
          if (idJwt) irParaFolha(idJwt);
          else navigate(Links.Estagiario_Minhas_Folhas, { replace: true });
        });
      return;
    }

    navigate(Links.Estagiario_Folha_Atual, { replace: true });
  }, [ehEstagiario, navigate, sessionReady, viewReady, context.state?.username]);

  const podeNotificar = useMemo(
    () =>
      podeExibirNotificacoesFolhasEmMassa(authVisao, effectiveView) &&
      Boolean(context.state?.username && registros && registros.length > 0),
    [authVisao, effectiveView, context.state?.username, registros],
  );

  useEffect(() => {
    if (!loaded) return;

    setActions({
      primary: podeNotificar
        ? {
          label: 'Notificações',
          variant: 'primary',
          onClick: () => setShowModalNotificacoes(true),
        }
        : undefined,
      print: {
        label: 'Imprimir',
        onClick: () => {
          if (!registros) return;
          exportFromJsxToPdf(
            <AttendanceSheetsPerMonthPdf
              mesBusca={mesBusca}
              registrosVisiveis={registros[0] ?? []}
              totalRegistros={registros[1] ?? 0}
              visibleColumnKeys={visibleColumnKeys}
            />,
          );
        },
      }
    });

    return () => setActions({});
  }, [
    loaded,
    mesBusca,
    podeNotificar,
    registros,
    context.state.username,
    setActions,
    setShowModalNotificacoes,
    visibleColumnKeys,
  ]);

  if (!loaded || ehEstagiario) return <Loading />;

  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <h3>Folhas de Frequências</h3>
          <span className="blockquote-footer pb-4 text-end">
            Registros de presenças e faltas dos estagiários (por mês).
          </span>
        </div>

        <div className="col-md-4">
          <br />
        </div>
      </div>
      <hr />

      <AttendanceSheetsFilters
        mesBusca={mesBusca}
        setMesBusca={setMesBusca}
        filtrosStatus={filtrosStatus}
        statusBusca={statusBusca}
        setStatusBusca={setStatusBusca}
        filtroResponsavel={filtroResponsavel}
        setFiltroResponsavel={setFiltroResponsavel}
        filtroEstagiario={filtroEstagiario}
        setFiltroEstagiario={setFiltroEstagiario}
        responsaveis={responsaveis}
        estagiarios={estagiarios}
        comFolha={comFolha}
        setComFolha={setComFolha}
        permissaoCriarFolhas={permissaoCriarFolhas}
        setShowModalCriarFolhas={setShowModalCriarFolhas}
        tableState={tableState}
        exibirFiltroResponsavel={exibirFiltroResponsavel}
      />

      {registros && (
        <AttendanceSheetsTable
          registros={registros}
          tableState={tableState}
          handleBuscar={handleBuscar}
          setSelectedItem={setSelectedItem}
          setShowModalCriarFolha={setShowModalCriarFolha}
          setFolhaSelecionada={setFolhaSelecionada}
          setShowModalResponsavel={setShowModalResponsavel}
          setShowModalNotificacaoIndividual={setShowModalNotificacaoIndividual}
          setFolhaParaAssinaturas={setFolhaParaAssinaturas}
          setShowModalAssinaturas={setShowModalAssinaturas}
          permissaoExcluirFolhas={permissaoExcluirFolhas}
          onVisibleColumnsChange={(keys) => setVisibleColumnKeys(keys)}
        />
      )}

      <AttendanceSheetsModals
        folhaSelecionada={folhaSelecionada}
        selectedItem={selectedItem}
        showModalResponsavel={showModalResponsavel}
        setShowModalResponsavel={setShowModalResponsavel}
        showModalNotificacaoIndividual={showModalNotificacaoIndividual}
        setShowModalNotificacaoIndividual={setShowModalNotificacaoIndividual}
        showModalCriarFolha={showModalCriarFolha}
        setShowModalCriarFolha={setShowModalCriarFolha}
        showModalCriarFolhas={showModalCriarFolhas}
        setShowModalCriarFolhas={setShowModalCriarFolhas}
        showModalNotificacoes={showModalNotificacoes}
        setShowModalNotificacoes={setShowModalNotificacoes}
        isExecuting={isExecuting}
        handleCriarFolha={handleCriarFolha}
        handleGenerateAttendanceSheetsForMonth={
          handleGenerateAttendanceSheetsForMonth
        }
        registros={registros}
        mesBusca={mesBusca}
        showModalAssinaturas={showModalAssinaturas}
        setShowModalAssinaturas={setShowModalAssinaturas}
        folhaParaAssinaturas={folhaParaAssinaturas}
        onResponsavelAlterado={handleBuscar}
      />

      {registros && registros.length > 0 && (
        <MassNotificationModal
          isOpen={showModalNotificacoes}
          onClose={() => setShowModalNotificacoes(false)}
          registrosVisiveis={registros?.[0] ?? []}
        />
      )}
    </>
  );
}
